/**
 * Module contract — the product's side of the dev overlay connection.
 *
 * Owns: carrying typed events from product code to whoever listens, holding
 *       the ones emitted before the overlay chunk has loaded, and a registry
 *       of commands product components provide while they are mounted.
 * Does not own: anything done with them — buffers, formatting and UI live in
 *       src/devOverlay. Product code never reads back from here.
 *
 * Transport only, and only in dev builds: with __DEV_OVERLAY__ false every
 * export is a no-op. Call sites still go inside `if (__DEV_OVERLAY__)` —
 * Rollup keeps a call to that no-op, and with it the event name, whenever an
 * argument might have side effects (a property read is enough).
 * vite.config.ts fails a prod bundle that still names one.
 */

import {
  DEV_COMMAND_NAMES,
  DEV_EVENT_NAMES,
  DEV_HOOK_MARKER,
} from "./devHookEventNames";

export type DevEventName = (typeof DEV_EVENT_NAMES)[number];

export type DevEventMap = {
  "ws.inbound": { chunkType: string; turnId: string | null };
  "ws.outbound": { chunkType: string | null; turnId: string | null };
  "ws.lifecycle": {
    phase: "open" | "close" | "error";
    code?: number;
    reason?: string;
  };
};

export type DevEvent = {
  [K in DevEventName]: { name: K; atMs: number; data: DevEventMap[K] };
}[DevEventName];

type DevEventListener = (event: DevEvent) => void;

/** Events before the first listener: lesson boot can start ahead of the overlay chunk. */
const MAX_PENDING_EVENTS = 200;

const listeners = new Set<DevEventListener>();
let pendingEvents: DevEvent[] = [];

/**
 * A broken listener must never break the code that delivered: the product
 * code that emitted, or mountDevOverlay subscribing.
 */
const deliver = (listener: DevEventListener, event: DevEvent): void => {
  try {
    listener(event);
  } catch (error) {
    console.warn(`${DEV_HOOK_MARKER} listener failed`, error);
  }
};

const emit = <K extends DevEventName>(name: K, data: DevEventMap[K]): void => {
  const event = { name, atMs: Date.now(), data } as DevEvent;
  if (listeners.size === 0) {
    pendingEvents.push(event);
    if (pendingEvents.length > MAX_PENDING_EVENTS) pendingEvents.shift();
    return;
  }
  for (const listener of listeners) deliver(listener, event);
};

/** The first subscriber also receives everything emitted before it. */
const subscribe = (listener: DevEventListener): (() => void) => {
  const isFirst = listeners.size === 0;
  listeners.add(listener);
  if (isFirst) {
    const replay = pendingEvents;
    pendingEvents = [];
    for (const event of replay) deliver(listener, event);
  }
  return () => listeners.delete(listener);
};

const noop = () => {};

/** Fire-and-forget; returns nothing so no product code can depend on it. */
export const emitDevEvent: <K extends DevEventName>(
  name: K,
  data: DevEventMap[K],
) => void = __DEV_OVERLAY__ ? emit : noop;

/** For src/devOverlay only. */
export const subscribeDevEvents: (listener: DevEventListener) => () => void =
  __DEV_OVERLAY__ ? subscribe : () => noop;

export type DevCommandName = (typeof DEV_COMMAND_NAMES)[number];

export type DevCommandParams = Record<string, string>;

/** Failures are values, never throws; the overlay's QaResult builds on this. */
export type DevCommandResult =
  | { ok: true; data?: unknown }
  | { ok: false; error: string };

type DevCommandHandler = (
  params: DevCommandParams,
) => Promise<DevCommandResult> | DevCommandResult;

const commands = new Map<DevCommandName, DevCommandHandler>();

/** The latest registration wins; unregistering an older one leaves it alone. */
const provide = (
  name: DevCommandName,
  handler: DevCommandHandler,
): (() => void) => {
  commands.set(name, handler);
  return () => {
    if (commands.get(name) === handler) commands.delete(name);
  };
};

const run = async (
  name: DevCommandName,
  params: DevCommandParams = {},
): Promise<DevCommandResult> => {
  const handler = commands.get(name);
  if (!handler) {
    return {
      ok: false,
      error: `${name} is not available here — its control is not on screen`,
    };
  }
  try {
    return await handler(params);
  } catch (error) {
    return {
      ok: false,
      error: `${DEV_HOOK_MARKER} ${name} threw: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
};

const unavailable = async (): Promise<DevCommandResult> => ({
  ok: false,
  error: "dev commands are not built into this bundle",
});

/**
 * For product components, from a useEffect inside `if (__DEV_OVERLAY__)`:
 * register while the UI control is mounted, return the unregister as cleanup.
 */
export const provideDevCommand: (
  name: DevCommandName,
  handler: DevCommandHandler,
) => () => void = __DEV_OVERLAY__ ? provide : () => noop;

/** For src/devOverlay only. */
export const runDevCommand: (
  name: DevCommandName,
  params?: DevCommandParams,
) => Promise<DevCommandResult> = __DEV_OVERLAY__ ? run : unavailable;

/** For src/devOverlay only: what is on screen right now. */
export const listDevCommands = (): DevCommandName[] =>
  __DEV_OVERLAY__ ? [...commands.keys()] : [];
